import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { selectUser } from "../../redux/slices/userSlice";
import { getUserRoles, hasRole } from "../../utils/roles";

const API = "https://api.sedihisham.com";

const emptyVacancy = {
  id: null,
  job_id: "",
  title_ar: "",
  title_en: "",
  location: "",
  description_ar: "",
  description_en: "",
  cv_required: false,
  active: true,
  sort_order: 0,
};

const inputClass =
  "w-full h-[34px] rounded border-[#c4c8cf] text-[14px] font-[400] placeholder:text-[12px]";

// Vacancies shown next to the job application form on the website. Each one
// is linked to a job of Job Management: "Apply now" picks that job.
function Vacancies() {
  const currentUser = useSelector(selectUser);
  const disabled = !hasRole(getUserRoles(currentUser), ["HR", "Admin"]);
  const [vacancies, setVacancies] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [values, setValues] = useState(emptyVacancy);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios
      .get(`${API}/careers/vacancies?all=1`)
      .then((res) => setVacancies(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setVacancies([]);
        setError("Could not load the vacancies — the server may need the latest update.");
      });
  };

  useEffect(() => {
    load();
    axios
      .get(`${API}/careers/jobmanagement/findall`)
      .then((res) => setJobs(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setValues({ ...values, [name]: type === "checkbox" ? checked : value });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!values.title_ar.trim()) {
      setError("Please enter the vacancy title in Arabic");
      return;
    }
    if (!values.job_id) {
      setError("Please choose the job this vacancy belongs to");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const url = values.id
        ? `${API}/careers/vacancies/${values.id}`
        : `${API}/careers/vacancies`;
      const res = await axios.post(url, values);
      setVacancies(Array.isArray(res.data) ? res.data : vacancies);
      setValues(emptyVacancy);
      Swal.fire({
        icon: "success",
        title: "Your changes had been saved",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Could not save the vacancy.");
    }
    setSaving(false);
  };

  const toggleActive = async (vacancy) => {
    try {
      const res = await axios.post(`${API}/careers/vacancies/${vacancy.id}`, {
        ...vacancy,
        active: !vacancy.active,
      });
      setVacancies(res.data);
    } catch (err) {
      setError("Could not update the vacancy.");
    }
  };

  const remove = async (vacancy) => {
    const answer = await Swal.fire({
      icon: "warning",
      title: `Delete "${vacancy.title_ar}"?`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!answer.isConfirmed) return;
    try {
      const res = await axios.get(`${API}/careers/vacancies/delete/${vacancy.id}`);
      setVacancies(res.data);
    } catch (err) {
      setError("Could not delete the vacancy.");
    }
  };

  const jobName = (id) => {
    const job = jobs.find((j) => j.id === Number(id));
    return job ? `${job.name_ar || ""}${job.name_en ? " — " + job.name_en : ""}` : "—";
  };

  return (
    <div className="w-[95%] pb-10">
      <form onSubmit={save} className="rounded-xl border border-[#E9F6FD] bg-[#fafdff] p-4">
        <p className="text-[15px] font-[700] text-[#033362]">
          {values.id ? "Edit vacancy" : "Add new vacancy"}
        </p>
        <p className="text-[12px] font-[400] text-[#707070] mb-3">
          Active vacancies are listed next to the application form on the
          website; "Apply now" selects the linked job.
        </p>
        <div className="grid grid-cols-2 gap-3 text-[14px] font-[400]">
          <label>
            Title - AR <span className="text-[red]">*</span>
            <input dir="rtl" name="title_ar" value={values.title_ar} onChange={change} className={inputClass} placeholder="مثال: مندوب مبيعات" />
          </label>
          <label>
            Title - EN
            <input name="title_en" value={values.title_en} onChange={change} className={inputClass} placeholder="e.g. Sales representative" />
          </label>
          <label>
            Linked job <span className="text-[red]">*</span>
            <select name="job_id" value={values.job_id || ""} onChange={change} className={inputClass}>
              <option value="">— Choose a job —</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name_ar} {job.name_en ? `— ${job.name_en}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>
            Location
            <input name="location" value={values.location || ""} onChange={change} className={inputClass} placeholder="مثال: دمشق" />
          </label>
          <label>
            Description - AR
            <textarea dir="rtl" name="description_ar" rows={3} value={values.description_ar || ""} onChange={change} className={`${inputClass} h-auto`} />
          </label>
          <label>
            Description - EN
            <textarea name="description_en" rows={3} value={values.description_en || ""} onChange={change} className={`${inputClass} h-auto`} />
          </label>
          <label>
            Order
            <input type="number" name="sort_order" value={values.sort_order} onChange={change} className={inputClass} />
          </label>
          <div className="flex items-end gap-6 pb-1">
            <label className="flex items-center gap-2">
              <input type="checkbox" name="cv_required" checked={values.cv_required} onChange={change} className="rounded" />
              CV required
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" name="active" checked={values.active} onChange={change} className="rounded" />
              Active (shown on the website)
            </label>
          </div>
        </div>
        {error ? (
          <p className="text-[14px] font-[400] mt-3 text-[red]">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 mt-3">
          {values.id && (
            <div onClick={() => setValues(emptyVacancy)} className="cursor-pointer rounded-full text-[13px] px-6 py-1 text-[#0388CC] bg-[#E9F6FD]">
              Cancel
            </div>
          )}
          <input type="submit" disabled={disabled || saving} value={values.id ? "Save" : "Add vacancy"} className="cursor-pointer rounded-full text-[13px] px-6 py-1 text-[#fff] bg-[#0388CC] disabled:opacity-50" />
        </div>
      </form>

      <table className="mt-6 w-full text-[14px] font-[400]">
        <thead>
          <tr className="text-left text-[#033362]">
            <th className="py-2">Title</th>
            <th>Linked job</th>
            <th>Location</th>
            <th>CV</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {vacancies === null ? (
            <tr><td className="py-3" colSpan={6}>Loading...</td></tr>
          ) : vacancies.length === 0 ? (
            <tr><td className="py-3" colSpan={6}>No vacancies yet.</td></tr>
          ) : (
            vacancies.map((vacancy) => (
              <tr key={vacancy.id}>
                <td className="py-2">
                  <div dir="rtl" className="text-left text-[#0388CC]">{vacancy.title_ar}</div>
                  <div className="text-[12px] text-[#707070]">{vacancy.title_en}</div>
                </td>
                <td>{jobName(vacancy.job_id)}</td>
                <td>{vacancy.location}</td>
                <td>{vacancy.cv_required ? "Required" : "—"}</td>
                <td>
                  <span
                    onClick={() => !disabled && toggleActive(vacancy)}
                    className={`cursor-pointer rounded px-3 py-[2px] text-[12px] text-[white] ${vacancy.active ? "bg-[#007530]" : "bg-[#9a9a9a]"}`}
                  >
                    {vacancy.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span onClick={() => !disabled && setValues({ ...emptyVacancy, ...vacancy, job_id: vacancy.job_id || "" })} className="cursor-pointer rounded px-3 py-[2px] mr-2 text-[white] bg-[#0388CC]">
                    <FontAwesomeIcon icon={faPen} />
                  </span>
                  <span onClick={() => !disabled && remove(vacancy)} className="cursor-pointer rounded px-3 py-[2px] text-[white] bg-[red]">
                    <FontAwesomeIcon icon={faTrash} />
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Vacancies;
